import { useState, useRef, useEffect, useContext } from "react"
import firebase from '../../firebase/compat'
import { db } from "../../firebase/config"
import { SavedIcon } from "../../StateManagment/SavedIcon"
import {
    buildLocationFromPdokDoc,
    combineToevoeging,
    PDOK_FL,
} from "../../helpers/pdokLocation"

// Match PDOK doc against user-entered toevoeging (huisletter or huisnummertoevoeging)
const docMatchesToevoeging = (doc, toevoeging) => {
    const wanted = (toevoeging || '').trim().toLowerCase()
    const docToev = combineToevoeging(doc.huisletter, doc.huisnummertoevoeging).toLowerCase()
    if (!wanted) return !docToev
    return wanted === docToev
}

/**
 * Postcode/huisnummer address fields with PDOK geocoding. Writes a `Location`
 * subdocument (incl. buurt/wijk/gemeente codes) onto the given doc. Reused for
 * the org address (CompagnyMeta) and per-activity locations (Activities).
 *
 * @param {object} props
 * @param {{ docid: string, Location?: object }} props.comp - the doc to edit (carries docid + optional Location)
 * @param {string} [props.collection] - Firestore collection to write to (default 'CompagnyMeta')
 * @param {boolean} [props.bare] - drop the settings card wrapper + "Adres" heading (for embedding under an existing label, e.g. the TOC node editor)
 */
const AdresFields = ({ comp, collection = 'CompagnyMeta', bare = false }) => {
    // Context
    const [, setSaved] = useContext(SavedIcon)

    // Existing values
    const loc = comp.Location || {}

    // State (controlled inputs — onChange updates state only, onBlur persists)
    const [postcode, setPostcode] = useState(loc.Postcode || '')
    const [huisnummer, setHuisnummer] = useState(loc.Huisnummer || '')
    const [toevoeging, setToevoeging] = useState(loc.HuisnummerToevoeging || '')
    const [straatnaam, setStraatnaam] = useState(loc.Straatnaam || '')
    const [woonplaats, setWoonplaats] = useState(loc.Woonplaats || '')
    const [status, setStatus] = useState('idle')   // idle | loading | error
    const [errorMsg, setErrorMsg] = useState('')

    // Refs
    const lastKeyRef = useRef(`${loc.Postcode || ''}|${loc.Huisnummer || ''}|${loc.HuisnummerToevoeging || ''}`)
    const abortRef = useRef(null)

    // Cancel any in-flight PDOK fetch on unmount so we don't setState on a dead component
    useEffect(() => {
        return () => {
            if (abortRef.current) abortRef.current.abort()
        }
    }, [])

    // Save a single Location subfield + mark geocode as stale.
    // Errors surface to the console + leave the SavedIcon hidden so the user
    // notices something is wrong (per CLAUDE.md "let errors be visible").
    const saveLocationField = (field, value) => {
        const update = { [`Location.${field}`]: value }
        if (field === 'Postcode' || field === 'Huisnummer' || field === 'HuisnummerToevoeging') {
            update['Location.GeocodeStatus'] = 'pending'
        }
        return db.collection(collection)
            .doc(comp.docid)
            .update(update)
            .then(() => setSaved('flex'))
            .catch((err) => {
                console.error(`Failed to save Location.${field}:`, err)
            })
    }

    // Save the full Location object (after a lookup) with a server timestamp
    const saveLocationObject = async (location) => {
        const update = {}
        Object.entries(location).forEach(([k, v]) => {
            update[`Location.${k}`] = v
        })
        update['Location.GeocodedAt'] = firebase.firestore.FieldValue.serverTimestamp()
        try {
            await db.collection(collection).doc(comp.docid).update(update)
        } catch (err) {
            console.error('Failed to save Location object:', err)
        }
    }

    // onChange: local state only (no Firestore write per keystroke)
    const onPostcodeChange = (e) => setPostcode(e.target.value)
    const onHuisnummerChange = (e) => setHuisnummer(e.target.value)
    const onToevoegingChange = (e) => setToevoeging(e.target.value)
    const onStraatnaamChange = (e) => setStraatnaam(e.target.value)
    const onWoonplaatsChange = (e) => setWoonplaats(e.target.value)

    // onBlur: persist + (for the lookup-driving fields) trigger PDOK lookup
    const onPostcodeBlur = async () => {
        await saveLocationField('Postcode', postcode)
        runLookup()
    }
    const onHuisnummerBlur = async () => {
        await saveLocationField('Huisnummer', huisnummer)
        runLookup()
    }
    const onToevoegingBlur = async () => {
        await saveLocationField('HuisnummerToevoeging', toevoeging)
        runLookup()
    }
    const onStraatnaamBlur = () => saveLocationField('Straatnaam', straatnaam)
    const onWoonplaatsBlur = () => saveLocationField('Woonplaats', woonplaats)

    const runLookup = async () => {
        const normalizedPostcode = postcode.replace(/\s+/g, '').toUpperCase()
        const normalizedHuisnummer = huisnummer.trim()
        const normalizedToevoeging = toevoeging.trim()
        const key = `${normalizedPostcode}|${normalizedHuisnummer}|${normalizedToevoeging}`

        if (!normalizedPostcode || !normalizedHuisnummer) return
        if (!/^[1-9][0-9]{3}[A-Z]{2}$/.test(normalizedPostcode)) return
        if (key === lastKeyRef.current) return
        if (status === 'loading') return

        if (abortRef.current) abortRef.current.abort()
        const controller = new AbortController()
        abortRef.current = controller

        setStatus('loading')
        setErrorMsg('')

        try {
            const query = `postcode:${normalizedPostcode} and huisnummer:${normalizedHuisnummer}`
            const url = `https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?q=${encodeURIComponent(query)}&fl=${PDOK_FL}&rows=20`

            const res = await fetch(url, { signal: controller.signal })
            const data = await res.json()
            const docs = data?.response?.docs || []

            const doc = docs.find((d) => docMatchesToevoeging(d, normalizedToevoeging)) || docs[0]

            if (!doc) {
                await saveLocationObject(buildLocationFromPdokDoc({}, {
                    postcode: normalizedPostcode,
                    huisnummer: normalizedHuisnummer,
                    toevoeging: normalizedToevoeging,
                    straatnaam,
                    woonplaats,
                    status: 'failed',
                }))
                setStatus('error')
                setErrorMsg('Adres niet gevonden, vul handmatig in')
                lastKeyRef.current = key
                return
            }

            const location = buildLocationFromPdokDoc(doc, {
                postcode: normalizedPostcode,
                huisnummer: normalizedHuisnummer,
                toevoeging: normalizedToevoeging,
            })
            await saveLocationObject(location)

            setStraatnaam(location.Straatnaam)
            setWoonplaats(location.Woonplaats)
            setStatus('idle')
            lastKeyRef.current = key
            setSaved('flex')
        } catch (err) {
            if (err.name === 'AbortError') return
            console.error('PDOK address lookup failed:', err)
            await saveLocationObject(buildLocationFromPdokDoc({}, {
                postcode: normalizedPostcode,
                huisnummer: normalizedHuisnummer,
                toevoeging: normalizedToevoeging,
                straatnaam,
                woonplaats,
                status: 'failed',
            }))
            setStatus('error')
            setErrorMsg('Opzoeken niet gelukt, vul handmatig in')
            lastKeyRef.current = key
        }
    }

    return (
        <div className={bare ? "node-editor-adres" : "dashboard-container logo-container"}>
            {!bare && <h2>Adres</h2>}
            <div className="settings-address-row">
                <input
                    className="input-classic"
                    type="text"
                    placeholder="Postcode"
                    value={postcode}
                    onChange={onPostcodeChange}
                    onBlur={onPostcodeBlur}
                />
                <input
                    className="input-classic"
                    type="text"
                    placeholder="Huisnummer"
                    value={huisnummer}
                    onChange={onHuisnummerChange}
                    onBlur={onHuisnummerBlur}
                />
                <input
                    className="input-classic"
                    type="text"
                    placeholder="Toevoeging"
                    value={toevoeging}
                    onChange={onToevoegingChange}
                    onBlur={onToevoegingBlur}
                />
            </div>
            <input
                className="input-classic"
                type="text"
                placeholder="Straatnaam"
                value={straatnaam}
                onChange={onStraatnaamChange}
                onBlur={onStraatnaamBlur}
            />
            <input
                className="input-classic"
                type="text"
                placeholder="Woonplaats"
                value={woonplaats}
                onChange={onWoonplaatsChange}
                onBlur={onWoonplaatsBlur}
            />
            {status === 'loading' && <p className="settings-address-status">Bezig met opzoeken...</p>}
            {status === 'error' && <p className="settings-address-error">{errorMsg}</p>}
        </div>
    )
}

export default AdresFields
